/**
 * useCatalogPrefs — per-browser catalogue preferences (brief-26).
 *
 * Scope decision (Stephan, 2026-07-25): pins, view mode and — later — the theme
 * are stored in localStorage, not on the broker API. There is no user-preferences
 * endpoint and building one is a cross-repo change; a browser-local preference is
 * the honest v1. Consequences, stated rather than hidden:
 *   • a pin does not follow the operator to another browser or device;
 *   • a pin is private to that browser, not editorial curation shared by the team.
 * Moving to a backend store later only changes this hook's body.
 *
 * The key is namespaced by instance slug AND user subject, so two operators on the
 * same machine, or the same operator across meta/tenant brokers, never collide.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

export type CatalogView = 'tiles' | 'list';
/** brief-28: 'system' follows prefers-color-scheme; the other two override it. */
export type ThemeChoice = 'system' | 'light' | 'dark';

export interface CatalogPrefs {
  pinned: string[];
  view: CatalogView;
  theme: ThemeChoice;
}

const EMPTY: CatalogPrefs = { pinned: [], view: 'tiles', theme: 'system' };

function keyFor(instanceSlug: string, userSub: string | null): string {
  return `dp.catalogPrefs.${instanceSlug}.${userSub ?? 'anon'}`;
}

/** localStorage throws in Safari private mode and when storage is disabled. */
function safeRead(key: string): CatalogPrefs {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<CatalogPrefs>;
    return {
      pinned: Array.isArray(parsed.pinned) ? parsed.pinned.filter((x) => typeof x === 'string') : [],
      view: parsed.view === 'list' ? 'list' : 'tiles',
      theme: parsed.theme === 'light' || parsed.theme === 'dark' ? parsed.theme : 'system',
    };
  } catch {
    return EMPTY;
  }
}

function safeWrite(key: string, value: CatalogPrefs): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* preference is lost on reload; never break the page over it */
  }
}

export function useCatalogPrefs(instanceSlug: string, userSub: string | null) {
  const key = useMemo(() => keyFor(instanceSlug, userSub), [instanceSlug, userSub]);
  const [prefs, setPrefs] = useState<CatalogPrefs>(() => safeRead(key));

  // Re-read when the identity behind the key changes (org switch, login).
  useEffect(() => { setPrefs(safeRead(key)); }, [key]);

  const persist = useCallback((next: CatalogPrefs) => {
    setPrefs(next);
    safeWrite(key, next);
  }, [key]);

  const togglePin = useCallback((id: string) => {
    setPrefs((cur) => {
      const next: CatalogPrefs = cur.pinned.includes(id)
        ? { ...cur, pinned: cur.pinned.filter((p) => p !== id) }
        : { ...cur, pinned: [...cur.pinned, id] };
      safeWrite(key, next);
      return next;
    });
  }, [key]);

  const setTheme = useCallback((theme: ThemeChoice) => {
    setPrefs((cur) => {
      const next = { ...cur, theme };
      safeWrite(key, next);
      return next;
    });
  }, [key]);

  const setView = useCallback((view: CatalogView) => {
    setPrefs((cur) => {
      const next = { ...cur, view };
      safeWrite(key, next);
      return next;
    });
  }, [key]);

  return { prefs, persist, togglePin, setView, setTheme };
}

/**
 * Pinned first, then the incoming order. Stable: a pin never reshuffles anything
 * except the pinned entry itself, so the catalogue does not appear to scramble.
 */
export function sortPinnedFirst<T extends { id: string }>(items: T[], pinned: string[]): T[] {
  if (pinned.length === 0) return items;
  const isPinned = (t: T) => pinned.includes(t.id);
  return [...items.filter(isPinned), ...items.filter((t) => !isPinned(t))];
}
